/**
 * PEOne Admin Push — 1:1 Port der Client-Logik aus PEBase/app-core.js
 * Token-Ziel: users/{uid}.fcmTokens (nie pebase_users)
 */
import { getMessaging, getToken, isSupported, Messaging } from 'firebase/messaging';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { app, db } from '../config/firebase';

export const PEONE_VAPID_PUBLIC_KEY =
  'BPurFmgt8stIr-UcoV5hoW173NeTH_rBC3eQFnCC6SFg7NRuzC5PvYaTwZN1SMRvfHLIpv4Vyh8H9c1XdqneINc';

const DEVICE_ID_KEY = 'pebase_device_id';
const SW_PATH = '/sw.js';

export type FcmTokenEntry = {
  token: string;
  os: string;
  deviceId: string;
  updatedAt: number;
};

export type PushStatusKind =
  | 'unsupported'
  | 'ios-install'
  | 'blocked'
  | 'active'
  | 'permission-no-device'
  | 'other-devices'
  | 'inactive';

let messagingInstance: Messaging | null = null;
let messagingInitTried = false;
let swMessageBound = false;

export function getDeviceOS(): string {
  const ua = navigator.userAgent || (navigator as any).vendor || (window as any).opera || '';
  if (/windows phone/i.test(ua)) return 'windows';
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) return 'ios';
  if (/Mac/.test(ua) && 'ontouchend' in document) return 'ios';
  if (/Mac/.test(ua)) return 'mac';
  if (/Win/i.test(ua)) return 'windows';
  if (/Linux/i.test(ua)) return 'linux';
  return 'other';
}

export function isPebaseStandalone(): boolean {
  try {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true
    );
  } catch {
    return false;
  }
}

export function isPebaseIOS(): boolean {
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) || (/Mac/.test(ua) && 'ontouchend' in document);
}

export function getPebaseDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id =
        (crypto.randomUUID && crypto.randomUUID()) ||
        `d-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return `tmp-${getDeviceOS()}`;
  }
}

async function getMessagingSafe(): Promise<Messaging | null> {
  if (messagingInstance) return messagingInstance;
  if (messagingInitTried) return null;
  messagingInitTried = true;
  try {
    const ok = await isSupported();
    if (!ok) return null;
    messagingInstance = getMessaging(app);
    return messagingInstance;
  } catch (e) {
    console.warn('[PEOne Push] messaging nicht verfügbar', e);
    return null;
  }
}

function ensureSwMessageListener(uid: string) {
  if (swMessageBound || !('serviceWorker' in navigator)) return;
  swMessageBound = true;
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'PEBASE_PUSH_SUBSCRIPTION_CHANGED') {
      console.log('[PEOne Push] Subscription rotiert — erneuere FCM-Token…');
      refreshFcmTokenIfAllowed(uid, true).catch(() => {});
    }
  });
}

export async function upsertFcmToken(uid: string, currentToken: string): Promise<void> {
  if (!currentToken || !uid) return;
  const currentOS = getDeviceOS();
  const deviceId = getPebaseDeviceId();
  const fresh = await getDoc(doc(db, 'users', uid));
  const existing: any[] = fresh.exists() ? fresh.data()?.fcmTokens || [] : [];
  const cleaned: FcmTokenEntry[] = [];
  const seenTok = new Set<string>();

  existing.forEach((t) => {
    const tok = typeof t === 'string' ? t : t?.token;
    if (!tok || seenTok.has(tok) || tok === currentToken) return;
    const did = typeof t === 'string' ? '' : String(t?.deviceId || '');
    if (did && did === deviceId) return;
    seenTok.add(tok);
    cleaned.push(
      typeof t === 'string'
        ? { token: t, os: 'other', deviceId: '', updatedAt: 0 }
        : { token: tok, os: t.os || 'other', deviceId: did, updatedAt: t.updatedAt || 0 }
    );
  });

  cleaned.push({ token: currentToken, os: currentOS, deviceId, updatedAt: Date.now() });

  const bestByDevice: Record<string, FcmTokenEntry> = {};
  const bestByOsFallback: Record<string, FcmTokenEntry> = {};
  cleaned.forEach((t) => {
    const did = String(t.deviceId || '');
    if (did) {
      if (!bestByDevice[did] || (t.updatedAt || 0) >= (bestByDevice[did].updatedAt || 0)) {
        bestByDevice[did] = t;
      }
      return;
    }
    const os = String(t.os || 'other').toLowerCase();
    if (!bestByOsFallback[os] || (t.updatedAt || 0) >= (bestByOsFallback[os].updatedAt || 0)) {
      bestByOsFallback[os] = t;
    }
  });
  const osCovered = new Set(
    Object.values(bestByDevice).map((t) => String(t.os || 'other').toLowerCase())
  );
  Object.entries(bestByOsFallback).forEach(([os, t]) => {
    if (!osCovered.has(os)) bestByDevice[`legacy-${os}`] = t;
  });
  const hasRealOs = Object.values(bestByDevice).some((t) => {
    const o = String(t.os || '').toLowerCase();
    return o && o !== 'other';
  });
  const capped = Object.values(bestByDevice)
    .filter((t) => {
      const o = String(t.os || 'other').toLowerCase();
      if (hasRealOs && o === 'other' && !t.deviceId) return false;
      return true;
    })
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
    .slice(0, 8);

  await setDoc(doc(db, 'users', uid), { fcmTokens: capped }, { merge: true });
}

export async function refreshFcmTokenIfAllowed(
  uid: string,
  force = false
): Promise<string | null> {
  if (!('Notification' in window) || Notification.permission !== 'granted') return null;
  if (!uid) return null;
  ensureSwMessageListener(uid);
  try {
    const messaging = await getMessagingSafe();
    if (!messaging) return null;
    const registration = await navigator.serviceWorker.register(SW_PATH, { scope: '/' });
    await navigator.serviceWorker.ready;
    const currentToken = await getToken(messaging, {
      vapidKey: PEONE_VAPID_PUBLIC_KEY,
      serviceWorkerRegistration: registration,
    });
    if (currentToken) {
      await upsertFcmToken(uid, currentToken);
      if (force) {
        console.log(
          '[PEOne Push] token refreshed',
          getDeviceOS(),
          getPebaseDeviceId(),
          currentToken.slice(0, 12) + '…'
        );
      }
      return currentToken;
    }
  } catch (e) {
    console.error('Token-Refresh Fehler:', e);
  }
  return null;
}

export type ActivatePushResult = {
  ok: boolean;
  message: string;
  token?: string | null;
};

export async function activatePush(uid: string): Promise<ActivatePushResult> {
  if (!('Notification' in window)) {
    return { ok: false, message: 'Dein Browser unterstützt keine Web-Push Benachrichtigungen.' };
  }
  if (!uid) {
    return { ok: false, message: 'Nicht angemeldet.' };
  }

  try {
    const ios = isPebaseIOS();
    const standalone = isPebaseStandalone();
    if (ios && !standalone) {
      return {
        ok: false,
        message:
          'iPhone: Push geht nur in der Home-Bildschirm-App. Safari → Teilen → „Zum Home-Bildschirm“, dann dort Push aktivieren.',
      };
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      try {
        localStorage.setItem('peone_push_setup_done', '1');
        localStorage.removeItem('peone_push_prompt_later');
      } catch { /* ignore */ }

      const currentToken = await refreshFcmTokenIfAllowed(uid, true);
      if (currentToken) {
        return {
          ok: true,
          token: currentToken,
          message: ios
            ? 'iPhone-Push aktiviert! App schließen und Presence testen — Desktop behält seinen Push.'
            : `Push aktiviert (${getDeviceOS()}). Andere Geräte bleiben parallel aktiv.`,
        };
      }
      return { ok: false, message: 'Fehler: Google lieferte leeren Token zurück.' };
    }
    return { ok: false, message: 'Berechtigung verweigert.' };
  } catch (e: any) {
    console.error('Push Fehler:', e);
    return { ok: false, message: 'Technischer Fehler: ' + (e?.message || String(e)) };
  }
}

export async function readPushStatus(uid: string): Promise<{
  kind: PushStatusKind;
  label: string;
  detail: string;
  buttonText: string;
}> {
  const unsupported = {
    kind: 'unsupported' as const,
    label: 'Push: nicht verfügbar',
    detail: 'Dieser Browser unterstützt keine Web-Push-Benachrichtigungen.',
    buttonText: 'Nicht verfügbar',
  };
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return unsupported;

  const ios = isPebaseIOS();
  const standalone = isPebaseStandalone();
  const permission = Notification.permission;
  const currentOS = getDeviceOS();
  const deviceId = getPebaseDeviceId();

  let tokens: FcmTokenEntry[] = [];
  if (uid) {
    try {
      const snap = await getDoc(doc(db, 'users', uid));
      const raw = snap.exists() ? snap.data()?.fcmTokens || [] : [];
      tokens = (Array.isArray(raw) ? raw : []).map((t: any) =>
        typeof t === 'string'
          ? { token: t, os: 'other', deviceId: '', updatedAt: 0 }
          : {
              token: String(t?.token || ''),
              os: String(t?.os || 'other'),
              deviceId: String(t?.deviceId || ''),
              updatedAt: Number(t?.updatedAt || 0),
            }
      ).filter((t: FcmTokenEntry) => !!t.token);
    } catch { /* ignore */ }
  }

  const thisDevice = tokens.find((t) => t.deviceId && t.deviceId === deviceId);
  const hasAny = tokens.length > 0;

  if (ios && !standalone && permission !== 'granted') {
    return {
      kind: 'ios-install',
      label: 'Push: iPhone noch nicht bereit',
      detail: 'Zuerst „Zum Home-Bildschirm“, dann in der installierten App aktivieren.',
      buttonText: 'Install-Hinweis',
    };
  }
  if (permission === 'denied') {
    return {
      kind: 'blocked',
      label: 'Push: blockiert',
      detail: 'Browser hat Benachrichtigungen verweigert. In den System-/Browser-Einstellungen für pe-one-app.web.app erlauben.',
      buttonText: 'Blockiert',
    };
  }
  if (permission === 'granted' && thisDevice) {
    return {
      kind: 'active',
      label: 'Push: aktiv auf diesem Gerät',
      detail: `Dieses Gerät (${currentOS}) ist verbunden.`,
      buttonText: 'Erneut registrieren',
    };
  }
  if (permission === 'granted' && !thisDevice) {
    return {
      kind: 'permission-no-device',
      label: 'Push: Erlaubnis da, Gerät fehlt',
      detail: 'Browser erlaubt Push, aber dieses Gerät ist noch nicht in der Liste.',
      buttonText: 'Gerät verbinden',
    };
  }
  if (hasAny) {
    return {
      kind: 'other-devices',
      label: 'Push: auf anderen Geräten aktiv',
      detail: 'Andere Geräte sind verbunden — dieses Gerät noch nicht.',
      buttonText: 'Dieses Gerät aktivieren',
    };
  }
  return {
    kind: 'inactive',
    label: 'Push: noch nicht aktiv',
    detail: 'Noch kein Gerät eingerichtet. Tippe auf Aktivieren.',
    buttonText: 'Lockscreen-Push aktivieren',
  };
}

/** Badge löschen beim App-Öffnen (wie PEBase). */
export async function clearPushBadges(): Promise<void> {
  try {
    if ((navigator as any).clearAppBadge) await (navigator as any).clearAppBadge();
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        const notes = await reg.getNotifications();
        notes.forEach((n) => n.close());
      }
    }
  } catch { /* ignore */ }
}
